import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { HttpUtils } from 'app/shared/http-utils';
import { Observable } from 'rxjs';
import {
  IAvatar,
  IContact,
  IContactResponse,
  IFriendsResponse,
  IFriendStatus,
  IRequestsResponse
} from '../model/friends.model';

@Injectable({
  providedIn: 'root',
})
export class FriendsService {
  private http = inject(HttpClient);
  private apiUrl = HttpUtils.getApiUrl('/followers/v1/friends');

  friendsTotal = signal(0)
  requestTotal = signal(0)
  contactsTotal = signal(0)

  getFriends(
    searchRequest?: string,
    take: number = 20,
    skip: number = 0,
    status?: IFriendStatus,
  ): Observable<IFriendsResponse> {
    let params = new HttpParams()
      .set('take', take)
      .set('skip', skip)
      .set('asc', true);

    if (searchRequest?.length) {
      params  = params.set('search', searchRequest);
    }

    if (status) {
      params  = params.set('status', status);
    }

    return this.http.get<IFriendsResponse>(this.apiUrl, { params });
  }

  setFriendsTotal(total: number) {
    this.friendsTotal.set(total);
  }

  getFriendsTotal() {
    return this.friendsTotal()
  }

  setRequestTotal(total: number) {
    this.requestTotal.set(total);
  }

  getRequestTotal() {
    return this.requestTotal()
  }

  setContactsTotal(total: number) {
    this.contactsTotal.set(total);
  }

  getContactsTotal() {
    return this.contactsTotal()
  }

  getFriendsById(id: number, take: number = 20, skip: number = 0): Observable<IFriendsResponse> {
    let params = new HttpParams().set('take', take).set('skip', skip).set('asc', true)

    if (id) {
      params = params.set('friendId', id);
    }

    return this.http.get<IFriendsResponse>(this.apiUrl, { params });
  }

  removeFromFriends(friendId: string) {
    return this.http.delete(this.apiUrl + '/' + friendId);
  }

  addFriend(friendId: string) {
    const params = new HttpParams().set('followerId', friendId);

    return this.http.post(`${this.apiUrl}/${friendId}`, { params });
  }

  shareTo(friendId: string) {
    const params = new HttpParams().set('followerId', friendId);

    return this.http.post(`${this.apiUrl}/${friendId}/share-to`, { params });
  }

  stopShareFrom(followerId: string) {
    const params = new HttpParams().set('followerId', followerId);

    return this.http.post(`${this.apiUrl}/${followerId}/stop-share-from`, { params });
  }

  unfollowUser(followerId: string) {
    const params = new HttpParams().set('followerId', followerId);

    return this.http.put(`${this.apiUrl}/${followerId}/set-inactive`, { params });
  }

  getRequests(
    take: number = 20,
    skip: number = 0,
    status?: IFriendStatus,
    searchRequest?: string,
  ): Observable<IRequestsResponse> {
    let params = new HttpParams().set('take', take).set('skip', skip).set('asc', true);

    if (status) {
      params = params.set('status', status);
    }

    if (searchRequest) {
      params = params.set('search', searchRequest);
    }

    return this.http.get<IRequestsResponse>(`${this.apiUrl}/requests`, { params });
  }

  isUserFriend(userId: number) {
    const params = new HttpParams().set('followerId', userId);

    return this.http.get<IFriendsResponse>(`${this.apiUrl}/${userId}/is-friend`, { params });
  }

  getUsers(query = '', take = 10, skip = 0, isFriend = false): Observable<IFriendsResponse> {
    const params = new HttpParams()
      .set('take', take)
      .set('skip', skip)
      .set('isFriend', isFriend)
      .set('search', query);

    return this.http.get<IFriendsResponse>(HttpUtils.getApiUrl('/followers/v1/user'), { params });
  }

  getContacts(query = '', take = 10, skip = 0): Observable<IContactResponse> {
    let params = new HttpParams()
      .set('take', take)
      .set('skip', skip)

    if (query) {
      params = params.set('search', query);
    }

    return this.http.get<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts'), { params });
  }

  updateUserContact(contact: IContact): Observable<IContactResponse> {
    return this.http.post<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts'), {
      fullName: contact.fullName,
      phone: contact.phone,
      email: contact.email,
    });
  }

  sendUserInvite(contactId: string) {
    const params = new HttpParams().set('contactId', contactId);

    return this.http.post<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts/' + contactId + '/invite'), { params });
  }

  removeUserContact(contactId: string) {
    const params = new HttpParams().set('contactId', contactId);

    return this.http.delete<IContactResponse>(HttpUtils.getApiUrl('/followers/v1/contacts/' + contactId), { params });
  }
}
